// src/pages/Public/Volunteer/VolunteerAvailabilityForm.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, FormControl, InputLabel,
  Select, MenuItem, Grid, Alert, CircularProgress, Card, CardContent, 
  Container, Checkbox, FormControlLabel, FormGroup, Chip, Stack, Accordion,
  AccordionSummary, AccordionDetails
} from '@mui/material';
import { CheckCircle, AccessTime, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import backendURL from '../../../config';

const PublicAvailabilityForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Date and slot selections
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateSlotSelections, setDateSlotSelections] = useState({});
  
  const [formData, setFormData] = useState({
    response_notes: '',
    preferences: {
      event_type: ''
    }
  });

  useEffect(() => {
    loadFormData();
  }, [token]);

  const loadFormData = async () => {
    try {
      const response = await axios.get(
        `${backendURL}/api/public/availability?token=${token}`
      );
      
      setData(response.data.data);
      
      // Pre-fill with current selections
      if (response.data.data.current_assignments) {
        const assignments = response.data.data.current_assignments;
        const dates = Object.keys(assignments);
        setSelectedDates(dates);
        setDateSlotSelections(assignments);
      }
      
      if (response.data.data.preferences) {
        setFormData(prev => ({
          ...prev,
          response_notes: response.data.data.response_notes || '',
          preferences: {
            ...prev.preferences,
            ...response.data.data.preferences
          }
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleDateToggle = (date) => {
    setSelectedDates(prev => {
      if (prev.includes(date)) {
        // Remove date and its slot selections
        const newSelections = { ...dateSlotSelections };
        delete newSelections[date];
        setDateSlotSelections(newSelections);
        return prev.filter(d => d !== date);
      } else {
        // Add date
        return [...prev, date];
      }
    });
  };

  const handleSlotToggle = (date, slotId, slot) => {
    // Check if slot is full for this date
    const isFull = slot.is_full;
    const currentlySelected = dateSlotSelections[date]?.includes(slotId);
    
    if (isFull && !currentlySelected) {
      return; // Don't allow selection if full
    }

    setDateSlotSelections(prev => {
      const newSelections = { ...prev };
      if (!newSelections[date]) {
        newSelections[date] = [];
      }
      
      if (newSelections[date].includes(slotId)) {
        newSelections[date] = newSelections[date].filter(id => id !== slotId);
        // Clean up empty arrays
        if (newSelections[date].length === 0) {
          delete newSelections[date];
        }
      } else {
        newSelections[date] = [...newSelections[date], slotId];
      }
      
      return newSelections;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build date_slot_selections array
    const selections = selectedDates.map(date => ({
      date,
      slot_ids: dateSlotSelections[date] || []
    })).filter(sel => sel.slot_ids.length > 0);
    
    if (selections.length === 0) {
      setError('Please select at least one time slot for at least one date');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      await axios.post(
        `${backendURL}/api/public/availability?token=${token}`,
        {
          date_slot_selections: selections,
          ...formData
        }
      );
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimeRange = (startTime, endTime) => {
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getTotalSlotsSelected = () => {
    return Object.values(dateSlotSelections).reduce((sum, slots) => sum + slots.length, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" color="success.main" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1">
            Your availability has been updated successfully.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You have selected {getTotalSlotsSelected()} time slot{getTotalSlotsSelected() !== 1 ? 's' : ''} across {selectedDates.length} day{selectedDates.length !== 1 ? 's' : ''}.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Volunteer Availability Confirmation
          </Typography>
          
          {/* Event Info */}
          <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {data?.event.name}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    📅 Dates
                  </Typography>
                  <Typography variant="body1">
                    {new Date(data?.event.event_start_date).toLocaleDateString()} - {new Date(data?.event.event_end_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                {data?.event.event_start_time && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      🕐 Daily Hours
                    </Typography>
                    <Typography variant="body1">
                      {formatTimeRange(data.event.event_start_time, data.event.event_end_time)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    📍 Location
                  </Typography>
                  <Typography variant="body1">
                    {data?.event.location}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Volunteer Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Your Information
            </Typography>
            <Typography variant="body2">
              {data?.volunteer.full_name}<br/>
              {data?.volunteer.email}<br/>
              {data?.volunteer.phone_number}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Date and Time Slot Selection */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Select Your Available Dates and Time Slots *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                First select the dates you're available, then choose your time slots for each date.
              </Typography>
              
              {/* Date Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Step 1: Select Dates
                </Typography>
                <FormGroup>
                  {data?.event.event_dates.map((date) => (
                    <FormControlLabel
                      key={date}
                      control={
                        <Checkbox
                          checked={selectedDates.includes(date)}
                          onChange={() => handleDateToggle(date)}
                        />
                      }
                      label={formatDate(date)}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Time Slots by Date */}
              {selectedDates.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Step 2: Select Time Slots for Each Date
                  </Typography>
                  {selectedDates.map((date) => (
                    <Accordion key={date} defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography fontWeight={600}>
                            {formatDate(date)}
                          </Typography>
                          {dateSlotSelections[date]?.length > 0 && (
                            <Chip 
                              label={`${dateSlotSelections[date].length} slot${dateSlotSelections[date].length !== 1 ? 's' : ''} selected`}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormGroup>
                          {data?.time_slots.map((slot) => {
                            const isSelected = dateSlotSelections[date]?.includes(slot.id);
                            const isFull = slot.is_full && !isSelected;
                            
                            return (
                              <Card 
                                key={slot.id} 
                                sx={{ 
                                  mb: 2,
                                  border: isSelected ? '2px solid' : '1px solid',
                                  borderColor: isSelected ? 'primary.main' : 'grey.300',
                                  bgcolor: isFull ? 'grey.100' : 'white',
                                  opacity: isFull ? 0.6 : 1,
                                  cursor: isFull ? 'not-allowed' : 'pointer',
                                  '&:hover': {
                                    bgcolor: isFull ? 'grey.100' : isSelected ? 'primary.50' : 'grey.50'
                                  }
                                }}
                              >
                                <CardContent>
                                  <Grid container alignItems="center" spacing={2}>
                                    <Grid item>
                                      <Checkbox
                                        checked={isSelected}
                                        disabled={isFull}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleSlotToggle(date, slot.id, slot);
                                        }}
                                      />
                                    </Grid>
                                    <Grid 
                                      item 
                                      xs 
                                      onClick={() => !isFull && handleSlotToggle(date, slot.id, slot)}
                                      sx={{ cursor: isFull ? 'not-allowed' : 'pointer' }}
                                    >
                                      <Typography variant="body1" fontWeight={600}>
                                        {slot.slot_name}
                                      </Typography>
                                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                        <Chip
                                          icon={<AccessTime />}
                                          label={formatTimeRange(slot.start_time, slot.end_time)}
                                          size="small"
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={isFull ? 'Full' : `${slot.available_spots} spots left`}
                                          size="small"
                                          color={isFull ? 'error' : 'success'}
                                          variant={isFull ? 'filled' : 'outlined'}
                                        />
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </FormGroup>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
              
              {getTotalSlotsSelected() > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  You have selected {getTotalSlotsSelected()} time slot{getTotalSlotsSelected() !== 1 ? 's' : ''} across {selectedDates.length} day{selectedDates.length !== 1 ? 's' : ''}.
                </Alert>
              )}
            </Box>

            {/* Additional Notes - REMOVED Availability Type Dropdown */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Additional Notes"
                  value={formData.response_notes}
                  onChange={(e) => setFormData({
                    ...formData,
                    response_notes: e.target.value
                  })}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={submitting || getTotalSlotsSelected() === 0}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Submit Response'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicAvailabilityForm;